import { codigosPaises } from '@/data/codigosTelefones';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import validarNumeroTelefoneBaseadoMascara from '@/validators/validarNumeroTelefoneBaseadoMascara';
import {
  cpfYupValidationNaoObrigatorio,
  cpfYupValidationObrigatorio,
} from '@/components/molecules/TextFieldCPF';
import {
  confirmacaoSenhaYupValidation,
  senhaYupValidation,
} from '@/components/organisms/TextFieldCadastroSenha';

import { dataNascimentoYupValidation } from '@/components/molecules/TextFieldDataNascimento';
import useSelectIdentidadeGeneroHook from '@/components/molecules/SelectIdentidadeGenero/hook/useSelectIdentidadeGeneroHook';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import removerMascaraValores from '@/utils/removerMascaraValores';
import requisicaoSegura from '@/auth/requisicaoSegura';
import { APIResponse } from '@/services/types';
import { useSnackbar } from '@/hooks/useSnackbar';
import { FormUsuarioType } from '@/components/organisms/FormUsuario';
import { useSearchParams } from 'next/navigation';
import { useMetaTrackHook } from '@/hooks/useMetaTrackHook';
import { EtapaCadastroEnum } from '../components/organisms/CadastroUsuarioEtapas';
import { StatusMetaTrack } from '@/services/metaTrackService';

export type FormCadastrarUsuario = FormUsuarioType & {
  senha: string;
  confirmacaoSenha: string;
};

export const getValidationSchema = (
  mascaraNumeroPais: string,
  cpfObrigatorio: boolean,
) =>
  Yup.object().shape({
    nome: Yup.string().required('O nome é obrigatório'),
    cpf: cpfObrigatorio
      ? cpfYupValidationObrigatorio
      : cpfYupValidationNaoObrigatorio,
    email: Yup.string()
      .email('Email inválido')
      .required('O email é obrigatório'),
    dataNascimento: dataNascimentoYupValidation,
    telefone: Yup.string()
      .matches(
        validarNumeroTelefoneBaseadoMascara(mascaraNumeroPais),
        'Número de telefone inválido',
      )
      .required('Número de telefone é obrigatório'),
    tipoGeneroID: Yup.string().required('O gênero é obrigatório'),
    senha: senhaYupValidation,
    confirmacaoSenha: confirmacaoSenhaYupValidation,
  });

function useCadastrarPageHook({
  cpfObrigatorio = false,
}: {
  cpfObrigatorio?: boolean;
}) {
  const { mostrarSnackbar } = useSnackbar();
  const searchParams = useSearchParams();

  const [cadastrandoUsuario, setCadastrandoUsuario] = useState(false);
  const [paisSelecionado, setPaisSelecionado] = useState(codigosPaises[27]);

  const validationSchema = getValidationSchema(
    paisSelecionado?.formato,
    cpfObrigatorio,
  );
  const [opcoesIdentidadeGenero, setOpcoesIdentidadeGenero] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [identidadeGeneroSelecionada, setIdentidadeGeneroSelecionada] =
    useState('');

  const [usuarioAceitouOsTermos, setUsuarioAceitouOsTermos] = useState(false);

  const { enviarEventosMetaTrack } = useMetaTrackHook();

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    watch,
  } = useForm<FormCadastrarUsuario>({
    defaultValues: {
      senha: undefined,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    setValue('telefone', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paisSelecionado, setValue]);

  useSelectIdentidadeGeneroHook({
    identidadeGeneroSelecionada,
    opcoesIdentidadeGenero,
    setOpcoesIdentidadeGenero,
    setValue,
  });

  async function realizarCadastrar(
    form: FormCadastrarUsuario,
  ): Promise<boolean> {
    try {
      setCadastrandoUsuario(true);

      const valorCPF: {
        cpf: undefined | string;
      } = {
        cpf: undefined,
      };

      if (form?.cpf) {
        valorCPF.cpf = removerMascaraValores(form.cpf);
      }

      const retornoUsuarioBackend: APIResponse = await requisicaoSegura({
        endpoint: '/Usuario/Cadastrar',
        dataForm: {
          ...form,
          ...valorCPF,
          telefone: removerMascaraValores(form.telefone),
          tipoGeneroID: identidadeGeneroSelecionada,
        },
      });

      if (!retornoUsuarioBackend.sucesso)
        throw new Error(retornoUsuarioBackend.mensagem);

      enviarEventosMetaTrack('sign_up_success');

      return true;
    } catch (error: unknown) {
      if (error instanceof Error) {
        enviarEventosMetaTrack('sign_up_error');

        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }

      return false;
    } finally {
      setCadastrandoUsuario(false);
    }
  }

  async function aceitarTermos() {
    return setUsuarioAceitouOsTermos((estadoAnterior) => !estadoAnterior);
  }

  const metaTrackSignUpEventName: Record<EtapaCadastroEnum, StatusMetaTrack> = {
    [EtapaCadastroEnum.etapaDadosPessoais]: 'sign_up_personal_info_complete',
    [EtapaCadastroEnum.etapaContato]: 'sign_up_contact_complete',
    [EtapaCadastroEnum.etapaSenhaETermos]: 'sign_up_success',
  };

  return {
    cadastrandoUsuario,
    control,
    register,
    handleSubmit,
    setPaisSelecionado,
    usuarioAceitouOsTermos,
    errors,
    isValid,
    paisSelecionado,
    opcoesIdentidadeGenero,
    setIdentidadeGeneroSelecionada,
    aceitarTermos,
    realizarCadastrar,
    getValues,
    watch,
    searchParams,
    enviarEventosMetaTrack,
    metaTrackSignUpEventName,
  };
}

export type useCadastrarPageHookType = ReturnType<typeof useCadastrarPageHook>;

export default useCadastrarPageHook;
